import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ShoppingCart, Users, Plus, Edit2, Trash2, Send, PackageCheck, Ban, FileText, X, Link2,
} from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLE = {
  draft: "border-border text-muted-foreground",
  issued: "border-blue-500/40 text-blue-400",
  received: "border-profit/40 text-profit",
  cancelled: "border-loss/40 text-loss",
};

const emptyVendor = {
  name: "", contact_person: "", email: "", phone: "",
  address_street: "", address_city: "", address_state: "", address_zip: "",
  payment_terms: "Within 30 days due net", notes: "",
};

const emptyLine = { description: "", material: "", quantity: 1, unit: "Each", unit_price: 0, delivery_date: "" };

export default function PurchaseOrdersPage({ companyId, company }) {
  const [pos, setPos] = useState([]);
  const [stats, setStats] = useState({});
  const [vendors, setVendors] = useState([]);

  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [vendorForm, setVendorForm] = useState(emptyVendor);
  const [editingVendor, setEditingVendor] = useState(null);

  const [showPoDialog, setShowPoDialog] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [poVendorId, setPoVendorId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [poTerms, setPoTerms] = useState("");
  const [poNotes, setPoNotes] = useState("");
  const [poLines, setPoLines] = useState([{ ...emptyLine }]);
  const [emailOnIssue, setEmailOnIssue] = useState(true);
  const [saving, setSaving] = useState(false);

  const [matchPo, setMatchPo] = useState(null);
  const [matchData, setMatchData] = useState(null);

  const emptyDeliv = { name: "", street: "", city: "", state: "", zip: "" };
  const [poDeliv, setPoDeliv] = useState(emptyDeliv);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [standardNote, setStandardNote] = useState("");

  const loadData = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/purchase-orders`).then(res => {
      setPos(res.data.purchase_orders);
      setStats(res.data.stats);
    }).catch(() => {});
    api.get(`/companies/${companyId}/vendors`).then(res => setVendors(res.data)).catch(() => {});
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---------- Vendors ----------
  const openVendor = (v = null) => {
    setEditingVendor(v);
    setVendorForm(v ? { ...emptyVendor, ...v } : emptyVendor);
    setShowVendorDialog(true);
  };

  const saveVendor = async () => {
    if (!vendorForm.name.trim()) return toast.error("Vendor name is required");
    try {
      const payload = Object.fromEntries(Object.keys(emptyVendor).map(k => [k, vendorForm[k] || (k === "quantity" ? 0 : "")]));
      if (editingVendor) {
        await api.put(`/companies/${companyId}/vendors/${editingVendor.vendor_id}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post(`/companies/${companyId}/vendors`, payload);
        toast.success("Vendor created");
      }
      setShowVendorDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save vendor");
    }
  };

  const deleteVendor = async (v) => {
    try {
      await api.delete(`/companies/${companyId}/vendors/${v.vendor_id}`);
      toast.success("Vendor deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete vendor");
    }
  };

  // ---------- Purchase Orders ----------
  const openPo = (po = null) => {
    setEditingPo(po);
    setPoVendorId(po?.vendor_id || "");
    setPoNumber(po?.po_number || "");
    setPoDate(po?.date || new Date().toISOString().split("T")[0]);
    setPoTerms(po?.payment_terms ?? "");
    setPoNotes(po?.notes || "");
    setPoLines(po?.line_items?.length ? po.line_items.map(l => ({ ...l })) : [{ ...emptyLine }]);
    setPoDeliv({ ...emptyDeliv, ...(po?.delivery_address || {}) });
    setShowPoDialog(true);
  };

  const updateLine = (i, field, value) => {
    setPoLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  };

  const poTotal = poLines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const savePo = async () => {
    if (!poVendorId) return toast.error("Select a vendor");
    const lines = poLines.filter(l => l.description.trim());
    if (!lines.length) return toast.error("Add at least one line item with a description");
    setSaving(true);
    try {
      const payload = {
        vendor_id: poVendorId,
        po_number: poNumber,
        date: poDate,
        payment_terms: poTerms,
        notes: poNotes,
        delivery_address: Object.values(poDeliv).some(v => v && v.trim()) ? poDeliv : null,
        line_items: lines.map(l => ({
          description: l.description, material: l.material || "",
          quantity: Number(l.quantity) || 1, unit: l.unit || "Each",
          unit_price: Number(l.unit_price) || 0, delivery_date: l.delivery_date || "",
        })),
      };
      if (editingPo) {
        await api.put(`/companies/${companyId}/purchase-orders/${editingPo.po_id}`, payload);
        toast.success("Purchase order updated");
      } else {
        await api.post(`/companies/${companyId}/purchase-orders`, payload);
        toast.success("Purchase order created as draft");
      }
      setShowPoDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save purchase order");
    } finally {
      setSaving(false);
    }
  };

  const issuePo = async (po) => {
    try {
      const res = await api.post(`/companies/${companyId}/purchase-orders/${po.po_id}/issue`,
        { send_email_to_vendor: emailOnIssue });
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to issue");
    }
  };

  const receivePo = async (po) => {
    try {
      const res = await api.post(`/companies/${companyId}/purchase-orders/${po.po_id}/receive`);
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const cancelPo = async (po) => {
    try {
      const res = await api.post(`/companies/${companyId}/purchase-orders/${po.po_id}/cancel`);
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deletePo = async (po) => {
    try {
      await api.delete(`/companies/${companyId}/purchase-orders/${po.po_id}`);
      toast.success("Draft deleted");
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async (po) => {
    try {
      const res = await api.get(`/companies/${companyId}/purchase-orders/${po.po_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `PO-${po.po_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Failed to generate PDF"); }
  };

  // ---------- PO ↔ transaction matching ----------
  const openMatch = async (po) => {
    setMatchPo(po);
    setMatchData(null);
    try {
      const res = await api.get(`/companies/${companyId}/purchase-orders/${po.po_id}/matches`);
      setMatchData(res.data);
    } catch { toast.error("Failed to load matches"); }
  };

  const linkTxn = async (txnId) => {
    try {
      const res = await api.post(`/companies/${companyId}/purchase-orders/${matchPo.po_id}/link/${txnId}`);
      toast.success(res.data.message);
      openMatch(matchPo);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to link"); }
  };

  const unlinkTxn = async (txnId) => {
    try {
      await api.post(`/companies/${companyId}/purchase-orders/${matchPo.po_id}/unlink/${txnId}`);
      toast.success("Transaction unlinked");
      openMatch(matchPo);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to unlink"); }
  };

  // ---------- Standard payment note ----------
  const openNoteDialog = async () => {
    try {
      const res = await api.get(`/companies/${companyId}/po-settings`);
      setStandardNote(res.data.po_standard_note);
      setShowNoteDialog(true);
    } catch { toast.error("Failed to load payment note"); }
  };

  const saveNote = async () => {
    try {
      await api.put(`/companies/${companyId}/po-settings`, { po_standard_note: standardNote });
      toast.success("Payment instructions saved — they will appear on every PO");
      setShowNoteDialog(false);
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Purchase Orders</h1>
          <p className="text-muted-foreground mt-1">Issue purchase orders to your providers</p>
        </div>
        <div className="flex gap-2 text-sm text-muted-foreground items-center">
          <Button size="sm" variant="outline" className="border-border text-muted-foreground"
            onClick={openNoteDialog} data-testid="po-standard-note-btn">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Payment Instructions
          </Button>
          <Badge variant="outline" className="border-primary/40 text-primary" data-testid="po-open-value">
            Open value: ${(stats.open_value || 0).toLocaleString()}
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="pos">
        <TabsList className="bg-secondary">
          <TabsTrigger value="pos" data-testid="tab-purchase-orders">
            <ShoppingCart className="w-4 h-4 mr-1.5" /> Purchase Orders ({pos.length})
          </TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">
            <Users className="w-4 h-4 mr-1.5" /> Vendors ({vendors.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pos" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => vendors.length ? openPo() : toast.error("Create a vendor first (Vendors tab)")}
              className="btn-primary-gradient" data-testid="new-po-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Purchase Order
            </Button>
          </div>
          <Card className="border-border/50 glass">
            <CardContent className="p-0">
              {pos.length === 0 ? (
                <p className="text-sm text-muted-foreground p-8 text-center" data-testid="po-empty">
                  No purchase orders yet. Create a vendor, then issue your first PO.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50 hover:bg-transparent">
                        <TableHead className="text-muted-foreground text-xs uppercase">PO Number</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Date</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Vendor</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Total</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Billed</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pos.map(po => (
                        <TableRow key={po.po_id} className="border-border/30" data-testid={`po-row-${po.po_id}`}>
                          <TableCell className="text-sm text-white font-mono">{po.po_number}</TableCell>
                          <TableCell className="text-sm text-muted-foreground font-mono">{po.date}</TableCell>
                          <TableCell className="text-sm text-white">{po.vendor?.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${STATUS_STYLE[po.status]}`} data-testid={`po-status-${po.po_id}`}>
                              {po.status}
                            </Badge>
                            {po.emailed_to && <span className="text-[10px] text-muted-foreground ml-2">emailed</span>}
                          </TableCell>
                          <TableCell className="text-sm text-white font-mono text-right">${po.total.toLocaleString()}</TableCell>
                          <TableCell className={`text-sm font-mono text-right ${(po.billed_amount || 0) >= po.total ? 'text-profit' : (po.billed_amount || 0) > 0 ? 'text-warning' : 'text-muted-foreground'}`}
                            data-testid={`po-billed-${po.po_id}`}>
                            ${(po.billed_amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {po.status !== "draft" && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-400" title="Match bank transactions"
                                  onClick={() => openMatch(po)} data-testid={`po-match-${po.po_id}`}>
                                  <Link2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Download PDF"
                                onClick={() => downloadPdf(po)} data-testid={`po-pdf-${po.po_id}`}>
                                <FileText className="w-4 h-4" />
                              </Button>
                              {po.status === "draft" && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-400" title="Issue to vendor"
                                    onClick={() => issuePo(po)} data-testid={`po-issue-${po.po_id}`}>
                                    <Send className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299]" title="Edit"
                                    onClick={() => openPo(po)} data-testid={`po-edit-${po.po_id}`}>
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-loss" title="Delete draft"
                                    onClick={() => deletePo(po)} data-testid={`po-delete-${po.po_id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {po.status === "issued" && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-profit" title="Mark received"
                                    onClick={() => receivePo(po)} data-testid={`po-receive-${po.po_id}`}>
                                    <PackageCheck className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299]" title="Edit"
                                    onClick={() => openPo(po)} data-testid={`po-edit-${po.po_id}`}>
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-loss" title="Cancel PO"
                                    onClick={() => cancelPo(po)} data-testid={`po-cancel-${po.po_id}`}>
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendors" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openVendor()} className="btn-primary-gradient" data-testid="new-vendor-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Vendor
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full text-center py-8" data-testid="vendors-empty">
                No vendors yet. Add the providers you buy from.
              </p>
            )}
            {vendors.map(v => (
              <Card key={v.vendor_id} className="border-border/50 glass" data-testid={`vendor-card-${v.vendor_id}`}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">{v.name}</p>
                      {v.vendor_code && (
                        <p className="text-xs text-primary font-mono" data-testid={`vendor-code-${v.vendor_id}`}>
                          Vendor No. {v.vendor_code}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8b9299]" onClick={() => openVendor(v)}
                        data-testid={`vendor-edit-${v.vendor_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-loss" onClick={() => deleteVendor(v)}
                        data-testid={`vendor-delete-${v.vendor_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  {v.contact_person && <p className="text-xs text-muted-foreground">{v.contact_person}</p>}
                  {v.email && <p className="text-xs text-muted-foreground">{v.email}</p>}
                  {v.phone && <p className="text-xs text-muted-foreground">{v.phone}</p>}
                  {(v.address_city || v.address_state) && (
                    <p className="text-xs text-muted-foreground">
                      {[v.address_city, v.address_state, v.address_zip].filter(Boolean).join(", ")}
                    </p>
                  )}
                  <p className="text-xs text-primary/80 pt-1">{v.payment_terms}</p>
                  <div className="flex items-center gap-2 pt-2 border-t border-border/30 mt-2">
                    <Badge variant="outline" className="text-xs border-border text-muted-foreground" data-testid={`vendor-po-count-${v.vendor_id}`}>
                      {v.po_count || 0} PO{(v.po_count || 0) !== 1 ? "s" : ""}
                    </Badge>
                    <Badge variant="outline" className={`text-xs font-mono ${(v.total_spend || 0) > 0 ? "border-profit/40 text-profit" : "border-border text-muted-foreground"}`}
                      data-testid={`vendor-spend-${v.vendor_id}`}>
                      ${(v.total_spend || 0).toLocaleString()} total
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Vendor Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className="bg-card border-border max-w-lg" aria-describedby={undefined} data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">{editingVendor ? "Edit Vendor" : "New Vendor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {[
              ["name", "Vendor Name *"], ["contact_person", "Contact Person"], ["email", "Email"], ["phone", "Phone"],
              ["address_street", "Street Address"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-[#8b9299] text-sm">{l}</Label>
                <Input value={vendorForm[k] || ""} onChange={e => setVendorForm(f => ({ ...f, [k]: e.target.value }))}
                  className="mt-1 bg-secondary border-border text-white" data-testid={`vendor-${k}-input`} />
              </div>
            ))}
            <div className="grid grid-cols-3 gap-2">
              {[["address_city", "City"], ["address_state", "State"], ["address_zip", "ZIP"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-[#8b9299] text-sm">{l}</Label>
                  <Input value={vendorForm[k] || ""} onChange={e => setVendorForm(f => ({ ...f, [k]: e.target.value }))}
                    className="mt-1 bg-secondary border-border text-white" data-testid={`vendor-${k}-input`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-[#8b9299] text-sm">Payment Terms</Label>
              <Input value={vendorForm.payment_terms || ""} onChange={e => setVendorForm(f => ({ ...f, payment_terms: e.target.value }))}
                className="mt-1 bg-secondary border-border text-white" data-testid="vendor-payment-terms-input" />
            </div>
          </div>
          <Button onClick={saveVendor} className="btn-primary-gradient w-full" data-testid="save-vendor-btn">
            {editingVendor ? "Update Vendor" : "Create Vendor"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* PO Dialog */}
      <Dialog open={showPoDialog} onOpenChange={setShowPoDialog}>
        <DialogContent className="bg-card border-border max-w-3xl" aria-describedby={undefined} data-testid="po-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              {editingPo ? `Edit ${editingPo.po_number}` : "New Purchase Order"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[#8b9299] text-sm">Vendor *</Label>
                <Select value={poVendorId} onValueChange={setPoVendorId}>
                  <SelectTrigger className="mt-1 bg-secondary border-border text-white" data-testid="po-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {vendors.map(v => (
                      <SelectItem key={v.vendor_id} value={v.vendor_id} className="text-white">{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">PO Number</Label>
                <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="Auto (e.g. 2026847)"
                  className="mt-1 bg-secondary border-border text-white font-mono" data-testid="po-number-input" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Date</Label>
                <Input type="date" value={poDate} onChange={e => setPoDate(e.target.value)}
                  className="mt-1 bg-secondary border-border text-white" data-testid="po-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-[#8b9299] text-sm">Payment Terms</Label>
              <Input value={poTerms} onChange={e => setPoTerms(e.target.value)}
                placeholder={vendors.find(v => v.vendor_id === poVendorId)?.payment_terms || "Within 30 days due net"}
                className="mt-1 bg-secondary border-border text-white" data-testid="po-terms-input" />
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Delivery Address <span className="text-muted-foreground/60">(optional — defaults to your company address)</span></Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input placeholder="Recipient / site name" value={poDeliv.name}
                  onChange={e => setPoDeliv(d => ({ ...d, name: e.target.value }))}
                  className="bg-secondary border-border text-white text-sm" data-testid="po-deliv-name-input" />
                <Input placeholder="Street" value={poDeliv.street}
                  onChange={e => setPoDeliv(d => ({ ...d, street: e.target.value }))}
                  className="bg-secondary border-border text-white text-sm" data-testid="po-deliv-street-input" />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input placeholder="City" value={poDeliv.city}
                  onChange={e => setPoDeliv(d => ({ ...d, city: e.target.value }))}
                  className="bg-secondary border-border text-white text-sm" data-testid="po-deliv-city-input" />
                <Input placeholder="State" value={poDeliv.state}
                  onChange={e => setPoDeliv(d => ({ ...d, state: e.target.value }))}
                  className="bg-secondary border-border text-white text-sm" data-testid="po-deliv-state-input" />
                <Input placeholder="ZIP" value={poDeliv.zip}
                  onChange={e => setPoDeliv(d => ({ ...d, zip: e.target.value }))}
                  className="bg-secondary border-border text-white text-sm" data-testid="po-deliv-zip-input" />
              </div>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Line Items</Label>
              <div className="space-y-2 mt-1">
                {poLines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`po-line-${i}`}>
                    <Input placeholder="Description *" value={l.description}
                      onChange={e => updateLine(i, "description", e.target.value)}
                      className="col-span-4 bg-secondary border-border text-white text-sm" data-testid={`po-line-desc-${i}`} />
                    <Input placeholder="Qty" type="number" value={l.quantity}
                      onChange={e => updateLine(i, "quantity", e.target.value)}
                      className="col-span-1 bg-secondary border-border text-white text-sm" data-testid={`po-line-qty-${i}`} />
                    <Input placeholder="Unit" value={l.unit}
                      onChange={e => updateLine(i, "unit", e.target.value)}
                      className="col-span-2 bg-secondary border-border text-white text-sm" data-testid={`po-line-unit-${i}`} />
                    <Input placeholder="Unit price" type="number" value={l.unit_price}
                      onChange={e => updateLine(i, "unit_price", e.target.value)}
                      className="col-span-2 bg-secondary border-border text-white text-sm" data-testid={`po-line-price-${i}`} />
                    <Input type="date" value={l.delivery_date} title="Delivery date"
                      onChange={e => updateLine(i, "delivery_date", e.target.value)}
                      className="col-span-2 bg-secondary border-border text-white text-sm" data-testid={`po-line-deliv-${i}`} />
                    <Button variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-loss"
                      onClick={() => setPoLines(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev)}
                      data-testid={`po-line-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <Button size="sm" variant="outline" className="border-border text-muted-foreground"
                  onClick={() => setPoLines(prev => [...prev, { ...emptyLine }])} data-testid="po-add-line-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
                </Button>
                <p className="text-sm text-white font-mono" data-testid="po-total">
                  Total: <span className="text-primary font-bold">${poTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </p>
              </div>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Notes</Label>
              <Textarea value={poNotes} onChange={e => setPoNotes(e.target.value)} rows={2}
                placeholder="e.g. invoice submission instructions for the vendor"
                className="mt-1 bg-secondary border-border text-white" data-testid="po-notes-input" />
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Checkbox checked={emailOnIssue} onCheckedChange={(v) => setEmailOnIssue(!!v)}
                className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                data-testid="po-email-on-issue-checkbox" />
              Email PDF to vendor when issued
            </label>
          </div>
          <Button onClick={savePo} disabled={saving} className="btn-primary-gradient w-full" data-testid="save-po-btn">
            {saving ? "Saving..." : editingPo ? "Update Purchase Order" : "Create Draft"}
          </Button>
        </DialogContent>
      </Dialog>
      {/* Standard Payment Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="bg-card border-border max-w-lg" aria-describedby={undefined} data-testid="po-note-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Standard Payment Instructions</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            This note is printed on every purchase order PDF under "PAYMENT INSTRUCTIONS".
          </p>
          <Textarea value={standardNote} onChange={e => setStandardNote(e.target.value)} rows={5}
            className="bg-secondary border-border text-white" data-testid="po-standard-note-input" />
          <Button onClick={saveNote} className="btn-primary-gradient w-full" data-testid="save-standard-note-btn">
            Save Payment Instructions
          </Button>
        </DialogContent>
      </Dialog>

      {/* Match Dialog */}
      <Dialog open={!!matchPo} onOpenChange={(v) => !v && setMatchPo(null)}>
        <DialogContent className="bg-card border-border max-w-2xl" aria-describedby={undefined} data-testid="po-match-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              Match Transactions — {matchPo?.po_number}
            </DialogTitle>
          </DialogHeader>
          {!matchData ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              <div className="flex items-center gap-3 text-sm">
                <Badge variant="outline" className="border-border text-white font-mono">
                  Ordered: ${matchData.po_total.toLocaleString()}
                </Badge>
                <Badge variant="outline" className={`font-mono ${matchData.billed_amount >= matchData.po_total ? 'border-profit/40 text-profit' : 'border-warning/40 text-warning'}`}
                  data-testid="match-billed-badge">
                  Billed: ${matchData.billed_amount.toLocaleString()}
                </Badge>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Linked transactions ({matchData.linked.length})</p>
                {matchData.linked.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="match-no-linked">No transactions linked yet.</p>
                ) : matchData.linked.map(t => (
                  <div key={t.transaction_id} className="flex items-center justify-between py-2 border-b border-border/30 gap-3"
                    data-testid={`match-linked-${t.transaction_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{t.description}</p>
                      <p className="text-xs text-muted-foreground font-mono">{t.date} · ${t.amount.toLocaleString()}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-loss text-xs flex-shrink-0"
                      onClick={() => unlinkTxn(t.transaction_id)} data-testid={`match-unlink-${t.transaction_id}`}>
                      Unlink
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Suggested matches</p>
                {matchData.suggestions.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="match-no-suggestions">
                    No likely matches found among unlinked expense transactions.
                  </p>
                ) : matchData.suggestions.map(t => (
                  <div key={t.transaction_id} className="flex items-center justify-between py-2 border-b border-border/30 gap-3"
                    data-testid={`match-suggestion-${t.transaction_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{t.description}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {t.date} · ${t.amount.toLocaleString()}
                        {t.exact_amount && <span className="text-profit ml-2">exact amount</span>}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="border-primary/40 text-primary text-xs flex-shrink-0"
                      onClick={() => linkTxn(t.transaction_id)} data-testid={`match-link-${t.transaction_id}`}>
                      <Link2 className="w-3 h-3 mr-1" /> Link
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
